import React, { useCallback, useEffect, useState } from "react";
import {
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";

import {
  WorkbookDetailsDocument,
  useWorkbookDetailsQuery,
} from "../Workbook/gql/Query.generated";
import { WorkbookType, WorkbookVariantType } from "../Workbook";
import {
  QueryForWorkbookQuery,
  useQueryForWorkbookLazyQuery,
} from "../ReviewQuery/gql/Query.generated";
import { useDeleteWorkbookVariantMutation } from "../WorkbookVariantSidebar/gql/Mutation.variant.generated";
import { useCreateWorkbookVariantMutation } from "./gql/Mutation.generated";
import {
  WorkbookErrorParameterSet as MatchingParameterSet,
  WorkbookIntroducedParameterInput,
} from "types/types";

import Loading from "ui/Loading";
import Panel from "ui/Panel";
import PageContent from "ui/PageContent";
import PanelSection from "ui/PanelSection";
import Callout from "ui/Callout";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCircle1,
  faCircle2,
  faEdit,
  faExclamationCircle,
} from "@fortawesome/pro-solid-svg-icons";
import { useRoutes } from "utils/routes";
import { LocationState, QueryTextArea } from "../ReviewQuery";
import Button from "ui/Button";
import VariantNameEditPanel from "../VariantNameEditPanel";
import classNames from "classnames";
import PlannerSettingsPanel, { PlannerSettings } from "../PlannerSettingsPanel";
import { cleanupQuery } from "../ReviewQuery/util";
import CreatableSelect from "ui/CreatableSelect";
import ModalContainer from "ui/ModalContainer";
import WorkbookDiff from "features/Workbooks/WorkbookDiff";
import { DiffModeOption } from "types/WorkbookDiff";
import { useWorkbookVariantRewriteLazyQuery } from "./gql/Query.generated";
import PlannerHintsPanel from "../PlannerHintsPanel";
import InsightTile, { NodeInsightType, variantInsights } from "../InsightTile";
// eslint-disable-next-line no-restricted-imports -- pre-existing cross-feature deep import
import { RewriteDescription } from "features/Checks/IssueDetailPage/QueryTuningAutomatedExplain";

export type SelectedNamesType = {
  [key: number]: { name: string | null; value: string };
};
type ParameterNameOptionType = {
  label: string;
  value: string;
};
type QueryParameterType =
  QueryForWorkbookQuery["getQueryForWorkbook"]["parameters"];

const RewriteQuery = () => {
  const { workbookId, databaseId } = useParams();

  const { loading, error, data } = useWorkbookDetailsQuery({
    variables: { workbookId, databaseId },
  });

  if (loading || error) {
    return <Loading error={!!error} />;
  }

  const hasPgHintPlan =
    data.sharedPreloadLibraries?.value?.split(",").includes("pg_hint_plan") ??
    false;

  return (
    <RewriteQueryContent
      workbook={data.getWorkbookDetails}
      hasPgHintPlan={hasPgHintPlan}
    />
  );
};

const RewriteQueryContent = ({
  workbook,
  hasPgHintPlan,
}: {
  workbook: WorkbookType;
  hasPgHintPlan: boolean;
}) => {
  const baselineQuery = workbook.baselineQuery;
  const navigate = useNavigate();
  const location = useLocation();
  const { databaseWorkbookVariantRun } = useRoutes();

  const { databaseId } = useParams();
  const [errorMessage, setErrorMessage] = useState("");
  const [applyErrorMessage, setApplyErrorMessage] = useState("");
  const [rewriteErrorInsight, setRewriteErrorInsight] = useState(null);
  const [queryEdit, setQueryEdit] = useState(false);
  const state = location.state as LocationState;
  const originalQuery = baselineQuery.queryTextWithAlias;
  const [query, setQuery] = useState(state?.queryText ?? originalQuery);
  const [name, setName] = useState(state?.name ?? "");
  const [description, setDescription] = useState(state?.description ?? "");

  // For planner settings and hints
  const [plannerSettings, setPlannerSettings] = useState<PlannerSettings>(
    state?.plannerSettings ?? {},
  );
  const [showPlannerSettings, setShowPlannerSettings] = useState(false);
  const [showPlannerHints, setShowPlannerHints] = useState(false);

  // For parameter name/value match (when the variant is created using parameter values)
  const [showParameterValueModal, setShowParameterValueModal] = useState(false);
  const [selectedSetId, setSelectedSetId] = useState<string>(null);
  const [selectedNames, setSelectedNames] = useState<SelectedNamesType>(null);

  // Query for checking if the query is valid
  const [getQuery, { loading, error, data }] = useQueryForWorkbookLazyQuery();
  // Mutation for creating a new variant
  const [createWorkbookVariant, { loading: creatingVariant }] =
    useCreateWorkbookVariantMutation();
  // Query for getting a rewrite query
  const [getRewriteQuery, { data: rewriteData, loading: rewriteLoading }] =
    useWorkbookVariantRewriteLazyQuery();

  // For insights
  const [insights, setInsights] = useState(() => {
    return variantInsights(workbook.baselineQuery);
  });
  const [selectedInsightIdx, setSelectedInsightIdx] = useState(null);
  // Parameters the applied rewrites introduced
  const [introducedParameters, setIntroducedParameters] = useState<
    WorkbookIntroducedParameterInput[]
  >([]);

  // For WorkbookDiff component's mode
  const [defaultModeIndex, setDefaultModeIndex] = useState(0);

  // For automatically applying an insight
  const [searchParams, setSearchParams] = useSearchParams();
  const [hasAutoApplied, setHasAutoApplied] = useState(false);

  useEffect(() => {
    if (rewriteData?.getWorkbookVariantRewrite && selectedInsightIdx !== null) {
      setQueryEdit(true);
      setQuery(rewriteData.getWorkbookVariantRewrite.queryWithAlias);
      const introduced =
        rewriteData.getWorkbookVariantRewrite.introducedParameters;
      setIntroducedParameters((prev) =>
        prev.concat(
          introduced
            .filter((param) => !prev.some((p) => p.name === param.name))
            .map(({ name, value, type }) => ({ name, value, type })),
        ),
      );
      const newInsights = [...insights];
      newInsights[selectedInsightIdx].applied = true;
      setInsights(newInsights);
      setApplyErrorMessage("");
      setDefaultModeIndex(1);
      setSelectedInsightIdx(null);
    }
  }, [rewriteData, insights, selectedInsightIdx]);

  const handleQueryRewrite = useCallback(
    (insight: NodeInsightType) => {
      getRewriteQuery({
        variables: {
          variantId: baselineQuery.id,
          rewriteType: insight.type,
          insight: JSON.stringify(insight.insight),
        },
        onError: (error) => {
          setApplyErrorMessage(error.message);
          setRewriteErrorInsight({
            type: insight.type,
            insight: insight.insight,
          });
        },
      });
    },
    [getRewriteQuery, baselineQuery.id],
  );

  // Apply an insight with search params, using the result id of an insight
  useEffect(() => {
    const applyParam = searchParams.get("apply");
    if (applyParam === null || hasAutoApplied) {
      return;
    }

    const applyIndx = insights.findIndex((ins) => ins.resultId === applyParam);
    if (applyIndx === -1) {
      return;
    }

    setHasAutoApplied(true);
    setSelectedInsightIdx(applyIndx);
    handleQueryRewrite(insights[applyIndx]);

    searchParams.delete("apply");
    setSearchParams(searchParams, { replace: true });
  }, [
    handleQueryRewrite,
    hasAutoApplied,
    insights,
    searchParams,
    setSearchParams,
  ]);

  if (loading || error) {
    return <Loading error={!!error} />;
  }

  const handleCheckQuery = () => {
    if (query.trim() === "") {
      setErrorMessage("Query text is required");
      return;
    }
    getQuery({
      variables: {
        databaseId,
        queryText: cleanupQuery(query),
        baselineId: baselineQuery.id,
      },
      onCompleted: (data) => {
        setQueryEdit(false);
        setRewriteErrorInsight(null);
        if (data.getQueryForWorkbook.validQuery) {
          setErrorMessage("");
          setSelectedNames(null);
          setShowParameterValueModal(false);
        } else if (data.getQueryForWorkbook.errorParameterSets) {
          setErrorMessage("");
          setShowParameterValueModal(true);
          const { setId, names } = initialParameterSelection(
            data.getQueryForWorkbook.errorParameterSets,
            data.getQueryForWorkbook.parameters,
          );
          setSelectedSetId(setId);
          setSelectedNames(names);
        } else {
          setErrorMessage(data.getQueryForWorkbook.errorMessage);
          setSelectedNames(null);
          setShowParameterValueModal(false);
        }
      },
      onError: (error) => {
        setErrorMessage(error.message);
      },
    });
  };

  const handleRunExplain = () => {
    if (creatingVariant) {
      return;
    }
    if (!validQuery) {
      setErrorMessage("Query is not valid");
      return;
    }

    createWorkbookVariant({
      variables: {
        databaseId: workbook.databaseId,
        workbookId: workbook.id,
        name: name,
        description: description || null,
        baselineVariantId: baselineQuery.id,
        queryText: query,
        plannerSettings: plannerSettings,
        appliedInsights: insights
          .filter((ins) => ins.applied)
          .map((ins) => {
            return {
              node_id: ins.insight["id"],
              type: ins.type,
              result_id: ins.resultId,
            };
          }),
        introducedParameters,
      },
      refetchQueries: [
        {
          query: WorkbookDetailsDocument,
          variables: {
            workbookId: workbook.id,
            databaseId: workbook.databaseId,
          },
        },
      ],
      awaitRefetchQueries: true,
      onCompleted: (data) => {
        navigate(
          databaseWorkbookVariantRun(
            workbook.databaseId,
            workbook.id,
            data.createWorkbookVariant.id,
          ),
        );
      },
      onError: (error) => {
        setErrorMessage(error.message);
      },
    });
  };

  function handleUpdateQuery(newQuery: string) {
    setQueryEdit(true);
    setQuery(newQuery);
    setErrorMessage("");
  }

  const checkedQuery = data?.getQueryForWorkbook;
  const validQuery = checkedQuery?.validQuery && !queryEdit;
  const workbookTitle = (
    <VariantCreationHeader
      name={name}
      setName={setName}
      description={description}
      setDescription={setDescription}
    />
  );
  const editButton = (
    <Button bare onClick={() => setQueryEdit(true)}>
      <FontAwesomeIcon icon={faEdit} />
    </Button>
  );
  const options: DiffModeOption[] = [
    { value: "write", label: "Edit" },
    {
      value: "diff-unified",
      label: "Unified Diff",
    },
    {
      value: "diff-split",
      label: "Split Diff",
    },
  ];
  return (
    <PageContent
      windowTitle={`EXPLAIN Workbook: ${workbook.name}`}
      title={workbookTitle}
      pageCategory="workbooks"
      pageName="rewrite-query"
      layout="sidebar"
    >
      <Panel
        title={<CreateVariantSteps step="step1" />}
        secondaryTitle={validQuery && editButton}
        className="mt-1 flex-grow"
      >
        <PanelSection className="flex-grow flex flex-col items-stretch gap-2">
          {rewriteErrorInsight && (
            <Callout
              title="Apply changes failed: Check Manual Rewrite Steps"
              variant="warning"
              collapsible
              defaultCollapsed
            >
              <RewriteDescription
                insight={rewriteErrorInsight.insight}
                insightType={rewriteErrorInsight.type}
              />
            </Callout>
          )}
          {validQuery ? (
            <WorkbookDiff
              className="flex-grow"
              original=""
              modified={checkedQuery.normalizedQuery.replace(
                /\$[\d]+/g,
                (match) => {
                  const matchedParam = checkedQuery.parameters.find(
                    (param) => `$${param.ref}` === match,
                  );
                  return matchedParam ? `$${matchedParam.name}` : match;
                },
              )}
              options={[{ value: "read", label: "" }]}
              canExpand={false}
              isBaseline
            />
          ) : (
            <div className="relative flex-grow">
              <WorkbookDiff
                className="absolute -top-2 left-0 right-0 bottom-0 overflow-y-scroll"
                original={originalQuery}
                modified={query}
                options={options}
                canExpand={false}
                onChangeSQLEditor={handleUpdateQuery}
                defaultModeIndex={defaultModeIndex}
              />
            </div>
          )}
          {showParameterValueModal && (
            <ParameterValueModal
              onDismiss={() => setShowParameterValueModal(false)}
              handleUpdateQuery={handleUpdateQuery}
              existingNames={workbook.parameterRefAliases}
              baselineQuery={workbook.baselineQuery.queryTextWithAlias}
              selectedNames={selectedNames}
              setSelectedNames={setSelectedNames}
              normalizedQuery={checkedQuery.normalizedQuery}
              sets={data.getQueryForWorkbook.errorParameterSets}
              selectedSetId={selectedSetId}
              setSelectedSetId={setSelectedSetId}
            />
          )}
          {errorMessage && (
            <div className="text-[#C22426]">
              <FontAwesomeIcon icon={faExclamationCircle} /> {errorMessage}
            </div>
          )}
          <div>
            {validQuery ? (
              <Button
                bare
                className="btn btn-success"
                onClick={handleRunExplain}
                disabled={creatingVariant}
              >
                {creatingVariant ? "Running EXPLAIN..." : "Run EXPLAIN..."}
              </Button>
            ) : (
              <Button
                bare
                className="btn btn-success"
                onClick={handleCheckQuery}
                disabled={query === ""}
              >
                Verify Query
              </Button>
            )}
          </div>
        </PanelSection>
      </Panel>
      <div className="h-3">
        {/* margin for above panel; due to layout we can't specify margin there directly */}
      </div>
      {/* Sidebar */}
      <div className="2xl:w-[400px] w-[320px]">
        <div className="leading-7 font-medium">Query Advisor</div>
        {insights.length > 0 ? (
          insights.map((ins, idx) => {
            return (
              <InsightTile
                key={`insight-${idx}`}
                workbookId={workbook.id}
                insight={ins}
                loading={rewriteLoading}
                onApply={
                  validQuery
                    ? undefined
                    : () => {
                        setSelectedInsightIdx(idx);
                        handleQueryRewrite(ins);
                      }
                }
                error={applyErrorMessage}
              />
            );
          })
        ) : (
          <div>No insights found</div>
        )}
        <hr />
        {!validQuery && (
          <>
            <Callout className="!py-2 mb-4">
              Customize planner behavior by changing the settings and hints.
              Settings are only applied to the current session.
            </Callout>
            <Button
              bare
              onClick={() => setShowPlannerSettings(true)}
              className="btn btn-primary w-full !px-4 !py-2.5 !font-medium"
            >
              <FontAwesomeIcon icon={faEdit} className="mr-1" /> Edit Planner
              Settings
            </Button>
            <Button
              bare
              onClick={() => setShowPlannerHints(true)}
              className="btn btn-primary w-full !px-4 !py-2.5 !font-medium mt-2"
              disabled={!hasPgHintPlan}
            >
              <FontAwesomeIcon icon={faEdit} className="mr-1" /> Edit Planner
              Hints
            </Button>
            {!hasPgHintPlan && (
              <div className="mt-2">
                Unable to use Planner Hints — <code>pg_hint_plan</code> isn't
                loaded in <code>shared_preload_libraries</code> for this
                database.
                <br />
                <a
                  target="_blank"
                  rel="noopener"
                  href="https://pganalyze.com/docs/workbooks/planner-settings-and-hints#editing-planner-hints"
                >
                  Learn more in documentation
                </a>
              </div>
            )}
            {showPlannerSettings && (
              <PlannerSettingsPanel
                onDismiss={() => setShowPlannerSettings(false)}
                plannerSettings={plannerSettings}
                setPlannerSettings={setPlannerSettings}
              />
            )}
            {showPlannerHints && (
              <PlannerHintsPanel
                query={query}
                setQuery={setQuery}
                onDismiss={() => setShowPlannerHints(false)}
              />
            )}
          </>
        )}
        {Object.keys(plannerSettings).length > 0 ? (
          <>
            <div className="leading-7 font-medium mt-4">Planner Settings</div>
            <div className="grid gap-3">
              {Object.entries(plannerSettings).map(([key, value], i) => {
                return (
                  <div key={`planner-${i}`} className="flex">
                    <div className="grow">
                      <span className="font-mono">{key}</span>
                    </div>
                    <div className="font-mono">{value}</div>
                  </div>
                );
              })}
            </div>
          </>
        ) : (
          <div className="leading-7 font-medium mt-4">
            No custom planner settings
          </div>
        )}
      </div>
    </PageContent>
  );
};

const placeholderName = (ref: number) => `unnamed_${ref}`;

export const createDefaultSelectedNames = (
  set: MatchingParameterSet,
  parameters: QueryParameterType,
  autoAssign = false,
) => {
  const selected: { [key: number]: { name: string | null; value: string } } =
    {};
  set.matchingValues.forEach((val) => {
    // Names are only pre-filled when auto-assigning from a chosen parameter set;
    // otherwise the user picks or creates a name for each value.
    selected[val.ref] = {
      name: autoAssign ? val.names[0] ?? null : null,
      value: val.value,
    };
  });
  parameters?.forEach((param) => {
    selected[param.ref] = selected[param.ref] ?? {
      name: param.name,
      value: "-",
    };
  });
  return selected;
};

// Decides the initial state for the Assign Parameter Names modal. Only the
// literal values in the pasted query need naming, and auto-fill gives each
// literal the first existing name that used its value. We pre-select and
// auto-fill by default, except in two ambiguous cases where the user should
// name things by hand:
//
//  1. A value appears at more literals than there are existing names that used
//     it, so auto-fill (which gives every literal the same first name) can't
//     map them cleanly (e.g. three literals all 0 when only "offset" and
//     "limit" ever used 0). When names are at least as many as the literals,
//     auto-fill still merges them onto the first name, which is fine for a
//     value that is genuinely the same throughout the query.
//  2. A literal's auto-filled name is already used by a named param elsewhere
//     in the query (e.g. a new `jsonb_array_length(tags) = 0` whose value
//     matches an existing `offset` param), which would silently merge the new
//     literal into that param.
export const initialParameterSelection = (
  sets: MatchingParameterSet[],
  parameters: QueryParameterType,
) => {
  const firstSet = sets[0];
  const existingNames = new Set(parameters?.map((param) => param.name) ?? []);
  const namesByValue = new Map<string, Set<string>>();
  const refCountsByValue = new Map<string, number>();
  firstSet.matchingValues.forEach(({ value, names }) => {
    const known = namesByValue.get(value) ?? new Set<string>();
    names.forEach((name) => known.add(name));
    namesByValue.set(value, known);
    refCountsByValue.set(value, (refCountsByValue.get(value) ?? 0) + 1);
  });
  const hasSharedValue = Array.from(refCountsByValue).some(
    ([value, refCount]) => {
      const nameCount = namesByValue.get(value).size;
      return nameCount > 1 && refCount > nameCount;
    },
  );
  const collidesWithExisting = firstSet.matchingValues.some(
    ({ names }) => names.length > 0 && existingNames.has(names[0]),
  );
  const ambiguous = hasSharedValue || collidesWithExisting;
  return {
    setId: ambiguous ? null : firstSet.parameterSetId,
    names: createDefaultSelectedNames(firstSet, parameters, !ambiguous),
  };
};

export const ParameterValueModal = ({
  onDismiss,
  handleUpdateQuery,
  existingNames,
  baselineQuery,
  selectedNames,
  setSelectedNames,
  normalizedQuery,
  sets,
  selectedSetId,
  setSelectedSetId,
}: {
  onDismiss: () => void;
  handleUpdateQuery: (newQuery: string) => void;
  existingNames: string[];
  baselineQuery: string;
  selectedNames: SelectedNamesType;
  setSelectedNames: (names: SelectedNamesType) => void;
  normalizedQuery: string;
  sets: MatchingParameterSet[];
  selectedSetId: string;
  setSelectedSetId: (id: string) => void;
}) => {
  const [query, setQuery] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [focusedParamName, setFocusedParamName] = useState(undefined);
  // ref/value are identical across sets, so any set works as the row source.
  const rows = sets[0].matchingValues;
  // Existing params that use each value in any parameter set. Surfaced as
  // suggestions regardless of whether a set is chosen for auto-fill.
  const matchedNamesByRef: { [ref: number]: string[] } = {};
  sets.forEach((s) =>
    s.matchingValues.forEach(({ ref, names }) => {
      matchedNamesByRef[ref] = Array.from(
        new Set([...(matchedNamesByRef[ref] ?? []), ...names]),
      );
    }),
  );
  const namesFromSelectedNames = Object.values(selectedNames)
    .map((item) => item.name)
    .filter(Boolean);

  useEffect(() => {
    const newQuery = normalizedQuery.replace(/(?<=\$)\d+/g, (match) => {
      const matchedParam = selectedNames[match as unknown as number];
      return matchedParam?.name ?? placeholderName(match as unknown as number);
    });
    setQuery(newQuery);
  }, [selectedNames, normalizedQuery, setQuery]);
  const onSelectChange = (ref: number, val: string) => {
    const newSelectedNames = { ...selectedNames };
    newSelectedNames[ref] = { name: val, value: selectedNames[ref].value };
    // Validate if there are duplicate names with different values
    const nameValueMap = {} as Record<string, string>;
    let duplicateName = null;
    for (const key in newSelectedNames) {
      const { name, value } = newSelectedNames[key];
      if (nameValueMap[name]) {
        if (nameValueMap[name] !== value) {
          duplicateName = name;
        }
      } else {
        nameValueMap[name] = value;
      }
    }
    if (duplicateName) {
      setErrorMessage(
        `Name ${duplicateName} is used for two or more different values. You can assign the same name to multiple values, but please make sure this is intentional.`,
      );
    } else {
      setErrorMessage("");
    }

    setSelectedNames(newSelectedNames);
  };
  const changeSelectedParameterSet = (id: string) => {
    if (!id) {
      // Back to no set selected: clear every assigned name.
      setSelectedSetId(null);
      setSelectedNames(createDefaultSelectedNames(sets[0], null));
      return;
    }
    setSelectedSetId(id);
    const newSet = sets.find((val) => val.parameterSetId === id);
    // TODO: this should be passing the parameters as a second argument, but not easy to pass without major refactoring
    // For now, as this is only called with "Auto assign names from:" is changed, which less likely happen, leave it as null
    setSelectedNames(createDefaultSelectedNames(newSet, null, true));
  };
  const assignNames = () => {
    const unknownParamExists = rows.some((val) => !selectedNames[val.ref].name);
    if (unknownParamExists) {
      setErrorMessage(
        "Unable to assign names, there are still parameters with name unassigned",
      );
    } else {
      handleUpdateQuery(query);
      onDismiss();
    }
  };
  return (
    <ModalContainer
      title="Assign Parameter Names"
      layout="right sidebar"
      onClose={onDismiss}
    >
      <div className="flex flex-col gap-4">
        <Callout thin>
          The query was pasted with parameter values. Give each value a name
          below, or auto-fill them all from a parameter set that already uses
          these values. Actual EXPLAIN runs use the value from the parameter
          set, which may differ from the values shown here.
        </Callout>
        <div className="grid grid-cols-2 gap-1">
          <div>
            <div className="mb-1">New Query for this variant</div>
            <QueryTextArea
              queryText={query}
              focusedParamName={focusedParamName}
            />
          </div>
          <div>
            <div className="mb-1">Baseline Query</div>
            <QueryTextArea
              queryText={baselineQuery}
              focusedParamName={focusedParamName}
            />
          </div>
        </div>
        <div className="flex items-center gap-2">
          <label htmlFor="auto-assign-set" className="font-medium">
            Auto-fill names from a parameter set:
          </label>
          <div>
            <select
              id="auto-assign-set"
              value={selectedSetId ?? ""}
              onChange={(e) => changeSelectedParameterSet(e.target.value)}
            >
              <option value="">Select a parameter set</option>
              {sets.map((set) => {
                return (
                  <option key={set.parameterSetId} value={set.parameterSetId}>
                    {set.parameterSetName}
                  </option>
                );
              })}
            </select>
          </div>
        </div>
        <div className="flex-shrink flex-grow basis-4 min-h-[calc(100dvh-400px)] overflow-auto grid grid-cols-[min-content_200px_1fr] gap-2 items-center font-medium p-1 auto-rows-min">
          <div>ID</div>
          <div>Value</div>
          <div>Parameter Name</div>
          {Object.values(
            rows.map(({ ref, value }) => {
              const assignedName = selectedNames[ref].name;
              const refName = assignedName ?? placeholderName(ref);
              const matchedNames = matchedNamesByRef[ref] ?? [];
              // parameter name options to show in the list, like col1, col2, col3
              // grouped by if it's a matching value or not
              const otherNames = Array.from(
                new Set(namesFromSelectedNames.concat(existingNames)),
              ).filter((n) => !matchedNames.includes(n));
              const options = [
                ...(matchedNames.length > 0
                  ? [
                      {
                        label: "Existing names that use this value",
                        options: matchedNames.map((n) => ({
                          value: n,
                          label: n,
                        })),
                      },
                    ]
                  : []),
                {
                  label: "Other names",
                  options: otherNames.map((n) => ({
                    value: n,
                    label: n,
                  })),
                },
              ];
              return [
                <div
                  key={`ref-${ref}`}
                  className={
                    assignedName
                      ? undefined
                      : "text-[var(--color-gray-700)] italic"
                  }
                  onMouseEnter={() => setFocusedParamName(refName)}
                  onMouseLeave={() => setFocusedParamName(undefined)}
                >
                  {`$${refName}`}
                </div>,
                <div key={`value-${ref}`}>{value}</div>,
                <div key={`select-${ref}`}>
                  <CreatableSelect<ParameterNameOptionType>
                    value={
                      selectedNames[ref].name
                        ? {
                            label: selectedNames[ref].name,
                            value: selectedNames[ref].name,
                          }
                        : null
                    }
                    onChange={(newValue) => {
                      onSelectChange(ref, newValue.value);
                      setFocusedParamName(newValue.value);
                    }}
                    options={options}
                    placeholder="Select or set parameter name for this value"
                    onFocus={() => setFocusedParamName(refName)}
                    onBlur={() => setFocusedParamName(undefined)}
                  />
                </div>,
              ];
            }),
          )}
        </div>
        <div className="sticky bottom-0 flex justify-between gap-4 border-t border-[#e8e8ee] bg-white py-2">
          <Button bare className="btn btn-success" onClick={assignNames}>
            Assign Names
          </Button>
          {errorMessage && (
            <div className="text-[#C22426]">
              <FontAwesomeIcon icon={faExclamationCircle} /> {errorMessage}
            </div>
          )}
        </div>
      </div>
    </ModalContainer>
  );
};

export const VariantCreationHeaderWithVariant = ({
  variant,
}: {
  variant: WorkbookVariantType;
}) => {
  const [name, setName] = useState(variant.name);
  const [description, setDescription] = useState(variant.description || "");

  return (
    <VariantCreationHeader
      name={name}
      description={description}
      setName={setName}
      setDescription={setDescription}
      variant={variant}
    />
  );
};

export const VariantCreationHeader = ({
  name,
  description,
  setName,
  setDescription,
  variant,
}: {
  name: string;
  description: string;
  setName?: (name: string) => void;
  setDescription?: (desc: string) => void;
  variant?: WorkbookVariantType;
}) => {
  const { databaseId, workbookId } = useParams();
  const [showVariantNameEditPanel, setShowVariantNameEditPanel] =
    useState(false);
  const navigate = useNavigate();
  const { databaseWorkbookVariants } = useRoutes();

  const [deleteWorkbookVariant] = useDeleteWorkbookVariantMutation();

  function handleEditShow() {
    setShowVariantNameEditPanel(true);
  }
  function handleEditDismiss() {
    setShowVariantNameEditPanel(false);
  }
  function handleDeleteVariant() {
    if (!window.confirm("Cancel variant creation?")) {
      return;
    }
    if (variant) {
      deleteWorkbookVariant({
        variables: { variantId: variant.id },
        refetchQueries: [
          {
            query: WorkbookDetailsDocument,
            variables: { workbookId: workbookId, databaseId },
          },
        ],
        awaitRefetchQueries: true,
        onCompleted: () => {
          navigate(databaseWorkbookVariants(databaseId, workbookId));
        },
      });
    } else {
      navigate(databaseWorkbookVariants(databaseId, workbookId));
    }
  }

  return (
    <div className="flex flex-row justify-between items-center">
      <div>
        <h2 className="text-[22px] text-[#606060] m-0 py-[9px] font-medium overflow-visible leading-[26px]">
          {name || "Untitled Variant"}
        </h2>
        <div className="text-[14px] leading-5 font-normal flex">
          {description && (
            <span className="mr-2 max-w-[320px] truncate">{description}</span>
          )}
          <Button bare onClick={handleEditShow} variant="link">
            {description ? "Edit" : "Edit name and description"}
          </Button>
        </div>
      </div>
      <div className="flex justify-end">
        <Button
          bare
          onClick={handleDeleteVariant}
          className="text-[14px] text-[#606060] font-normal"
        >
          Cancel workflow
        </Button>
      </div>
      {showVariantNameEditPanel && (
        <VariantNameEditPanel
          onDismiss={handleEditDismiss}
          name={name}
          description={description}
          setName={setName}
          setDescription={setDescription}
          variant={variant}
        />
      )}
    </div>
  );
};

export const CreateVariantSteps = ({
  step,
  variant,
}: {
  step: "step1" | "step2";
  variant?: WorkbookVariantType;
}) => {
  const navigate = useNavigate();
  const { databaseWorkbookVariantNew } = useRoutes();
  const { databaseId, workbookId } = useParams();

  const active = "text-[#29426D]";
  const inactive = "text-[var(--color-gray-700)]";

  const step1Class = step === "step1" ? active : inactive;
  const step2Class = step === "step2" ? active : inactive;

  const [deleteWorkbookVariant] = useDeleteWorkbookVariantMutation();

  const handleBackToRewriteQuery = () => {
    if (
      window.confirm(
        "Going back to the Rewrite query step? All uploaded or executed EXPLAIN results will be cleared.",
      )
    ) {
      deleteWorkbookVariant({
        variables: {
          variantId: variant.id,
        },
        refetchQueries: [
          {
            query: WorkbookDetailsDocument,
            variables: {
              workbookId,
              databaseId,
            },
          },
        ],
        awaitRefetchQueries: true,
        onCompleted: () => {
          navigate(databaseWorkbookVariantNew(databaseId, workbookId), {
            state: {
              queryText: variant.queryTextWithAlias,
              name: variant.name,
              description: variant.description,
              plannerSettings: variant.plannerSettings,
            },
          });
        },
      });
    }
  };

  return (
    <div className="flex justify-center align-middle text-[14px] gap-4 font-medium">
      <Button
        bare
        className={classNames(step1Class, step !== "step1" && "cursor-pointer")}
        onClick={step !== "step1" ? handleBackToRewriteQuery : undefined}
      >
        <FontAwesomeIcon icon={faCircle1} /> Rewrite Query and Edit Planner
      </Button>
      <div className={step2Class}>
        <FontAwesomeIcon icon={faCircle2} /> Run EXPLAIN
      </div>
    </div>
  );
};

export default RewriteQuery;
